/**
 * ChatTransferSessionState.ts
 *
 * Pure-function state machine for the chat-transfer handshake.
 *
 * Mirrors `kaether_for_ios/Stoat/E2EE/ChatTransferSessionState.swift` —
 * a single `transition(state, event)` function that returns
 * `{ next, sideEffect }`. The side-effect channel keeps WebRTC plumbing
 * separate from the protocol: each terminal-relevant transition surfaces
 * the action the orchestrator should perform (start ICE, send bundle, wipe
 * keys, etc.) without this file knowing anything about RTCPeerConnection.
 *
 * State diagram:
 *
 * ```
 *   idle ──qrShown──▶ awaitingPeer
 *                       │
 *                ─peerKeyAgreed─▶ keyAgreed
 *                                  │
 *                          ─verificationReady(code)─▶ awaitingVerification
 *                                                       │     ▲
 *                                  ─userConfirmedCode──▶│     │
 *                                                       ▼     │
 *                                                    verified │
 *                                                       │     │ userRejectedCode
 *                                              ─startTransfer─▶│
 *                                                       ▼     │
 *                                                  transferring
 *                                                       │     │
 *                                          ─bundleSent─▶│     │
 *                                                       ▼     ▼
 *                                                   completed   aborted
 *
 *   any non-terminal + error(reason) → failed(reason)
 *   completed / aborted / failed are terminal — they absorb further events
 *   and return `{ next: <same>, sideEffect: null }`.
 * ```
 *
 * `idle` covers both pre-pairing and the role-agnostic seed state; the
 * web↔web flow does not distinguish "old device" vs "new device" at the
 * state-machine layer — both endpoints walk the same states.
 *
 * Time is supplied via `at: number` (unix ms) arguments. There is no
 * `Date.now()` call in this file so the self-test can replay arbitrary
 * clocks.
 */

// ---------------------------------------------------------------------------
// States
// ---------------------------------------------------------------------------

export type ChatTransferState =
  | { readonly kind: "idle"; readonly enteredAt: number }
  | { readonly kind: "awaitingPeer"; readonly enteredAt: number }
  | { readonly kind: "keyAgreed"; readonly enteredAt: number }
  | {
      readonly kind: "awaitingVerification";
      readonly enteredAt: number;
      readonly verificationCode: string;
    }
  | {
      readonly kind: "verified";
      readonly enteredAt: number;
      readonly verificationCode: string;
    }
  | {
      readonly kind: "transferring";
      readonly enteredAt: number;
      readonly verificationCode: string;
    }
  | { readonly kind: "completed"; readonly enteredAt: number }
  | { readonly kind: "aborted"; readonly enteredAt: number; readonly reason?: string }
  | { readonly kind: "failed"; readonly enteredAt: number; readonly reason: string };

// ---------------------------------------------------------------------------
// Events
// ---------------------------------------------------------------------------

export type ChatTransferEvent =
  | { readonly kind: "qrShown"; readonly at: number }
  | { readonly kind: "peerKeyAgreed"; readonly at: number }
  | {
      readonly kind: "verificationReady";
      readonly verificationCode: string;
      readonly at: number;
    }
  | { readonly kind: "userConfirmedCode"; readonly at: number }
  | { readonly kind: "userRejectedCode"; readonly at: number }
  | { readonly kind: "startTransfer"; readonly at: number }
  | { readonly kind: "bundleSent"; readonly at: number }
  | { readonly kind: "error"; readonly reason: string; readonly at: number };

// ---------------------------------------------------------------------------
// Side effects
// ---------------------------------------------------------------------------

/**
 * The orchestrator (which owns RTCPeerConnection, the QR canvas, the camera
 * scanner, the bundle serializer) consumes these and never reaches back into
 * the state machine. Each kind is named after the action — there is no
 * generic `doSomething` so call sites grep cleanly.
 *
 * The state machine returns `null` when no action is required (e.g. an
 * absorbed terminal event).
 */
export type ChatTransferSideEffect =
  | { readonly kind: "openSignalling" }
  | { readonly kind: "deriveTransferKey" }
  | { readonly kind: "displayVerificationCode"; readonly verificationCode: string }
  | { readonly kind: "openDataChannel" }
  | { readonly kind: "sendBundle" }
  | { readonly kind: "finishAndWipeKeys" }
  | { readonly kind: "abortAndWipeKeys"; readonly reason?: string }
  | { readonly kind: "failAndWipeKeys"; readonly reason: string };

// ---------------------------------------------------------------------------
// Transition result
// ---------------------------------------------------------------------------

export interface TransitionResult {
  readonly next: ChatTransferState;
  readonly sideEffect: ChatTransferSideEffect | null;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

export function initialState(at: number): ChatTransferState {
  return { kind: "idle", enteredAt: at };
}

function isTerminal(state: ChatTransferState): boolean {
  return (
    state.kind === "completed" ||
    state.kind === "aborted" ||
    state.kind === "failed"
  );
}

// ---------------------------------------------------------------------------
// transition
// ---------------------------------------------------------------------------

/**
 * Apply a single event. Returns the next state plus an optional side effect
 * the orchestrator should run.
 *
 * Forbidden / nonsensical transitions return the snapshot unchanged with
 * `sideEffect: null` — they are a no-op so a stale UI callback or a spurious
 * WebRTC event cannot drag a session into a half-baked state.
 *
 * Terminal states (`completed`, `aborted`, `failed`) absorb every event:
 * once dead, the session stays dead. The orchestrator must construct a fresh
 * `initialState` to start over.
 */
export function transition(
  state: ChatTransferState,
  event: ChatTransferEvent,
): TransitionResult {
  // Terminal absorb. Important so a stray late callback (e.g. a delayed
  // `bundleSent` that races with a `userRejectedCode`) cannot resurrect a
  // sealed session.
  if (isTerminal(state)) {
    return { next: state, sideEffect: null };
  }

  // Universal error fan-in. Anywhere off the happy path lands in `failed`.
  if (event.kind === "error") {
    return {
      next: { kind: "failed", enteredAt: event.at, reason: event.reason },
      sideEffect: { kind: "failAndWipeKeys", reason: event.reason },
    };
  }

  switch (state.kind) {
    case "idle":
      if (event.kind === "qrShown") {
        return {
          next: { kind: "awaitingPeer", enteredAt: event.at },
          sideEffect: { kind: "openSignalling" },
        };
      }
      return { next: state, sideEffect: null };

    case "awaitingPeer":
      if (event.kind === "peerKeyAgreed") {
        return {
          next: { kind: "keyAgreed", enteredAt: event.at },
          sideEffect: { kind: "deriveTransferKey" },
        };
      }
      return { next: state, sideEffect: null };

    case "keyAgreed":
      if (event.kind === "verificationReady") {
        return {
          next: {
            kind: "awaitingVerification",
            enteredAt: event.at,
            verificationCode: event.verificationCode,
          },
          sideEffect: {
            kind: "displayVerificationCode",
            verificationCode: event.verificationCode,
          },
        };
      }
      return { next: state, sideEffect: null };

    case "awaitingVerification":
      if (event.kind === "userConfirmedCode") {
        return {
          next: {
            kind: "verified",
            enteredAt: event.at,
            verificationCode: state.verificationCode,
          },
          sideEffect: { kind: "openDataChannel" },
        };
      }
      if (event.kind === "userRejectedCode") {
        return {
          next: { kind: "aborted", enteredAt: event.at, reason: "userRejectedCode" },
          sideEffect: { kind: "abortAndWipeKeys", reason: "userRejectedCode" },
        };
      }
      return { next: state, sideEffect: null };

    case "verified":
      if (event.kind === "startTransfer") {
        return {
          next: {
            kind: "transferring",
            enteredAt: event.at,
            verificationCode: state.verificationCode,
          },
          sideEffect: { kind: "sendBundle" },
        };
      }
      return { next: state, sideEffect: null };

    case "transferring":
      if (event.kind === "bundleSent") {
        return {
          next: { kind: "completed", enteredAt: event.at },
          sideEffect: { kind: "finishAndWipeKeys" },
        };
      }
      return { next: state, sideEffect: null };

    // Already filtered by isTerminal above, but TypeScript wants the switch
    // to be exhaustive over the union.
    case "completed":
    case "aborted":
    case "failed":
      return { next: state, sideEffect: null };
  }
}
